import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Aula, Bloqueo, Reserva } from '../types/aulas';
import { useEffect } from 'react';

/** ---------- Tipos auxiliares ---------- */
export type AulaInput = {
  id_centro: number;
  nombre: string;
  capacidad: number;
  capacidad_cat: 'S' | 'M' | 'L';
  planta?: number;
  zona?: string;
  /** ← prioridad del aula */
  prioridad?: 'ALTA' | 'MEDIA' | 'BAJA';
  usoAula?: 'MIXTA' | 'SOLO_ONLINE' | 'SOLO_PRESENCIAL';
  internetPerfil?: 'SIN_INTERNET' | 'BASICO' | 'ESTABLE';
  internetObservaciones?: string;
};

export interface PlanificadorData {
  aulas: Array<{
    id: number;
    nombre: string;
    capacidad: number;
    prioridad: 'ALTA' | 'MEDIA' | 'BAJA';
    uso_aula?: 'MIXTA' | 'SOLO_ONLINE' | 'SOLO_PRESENCIAL';
    internet_perfil?: 'SIN_INTERNET' | 'BASICO' | 'ESTABLE';
    internet_observaciones?: string | null;
  }>;
  reservas: Pick<
    Reserva,
    | 'id'
    | 'id_aula'
    | 'proyecto_id'
    | 'id_profesor'
    | 'fecha'
    | 'hora_inicio'
    | 'hora_fin'
    | 'num_alumnos'
    | 'aula_estado'
    | 'id_centro'
  >[];
  bloqueos: Bloqueo[];
}

/** ---------- Queries ---------- */
export const useAulasByCentro = (centroId: number) =>
  useQuery<Aula[]>({
    queryKey: ['centros', centroId, 'aulas'],
    queryFn: () => api.get<Aula[]>(`/aulas/centro/${centroId}`),
    enabled: !!centroId,
  });

export const useAula = (id: number | null | undefined) =>
  useQuery<Aula>({
    queryKey: ['aulas', id ?? 0],
    queryFn: () => api.get<Aula>(`/aulas/${id}`),
    enabled: !!id,
  });

export const useBloqueos = (aulaId: number) =>
  useQuery<Bloqueo[]>({
    queryKey: ['aulas', aulaId, 'bloqueos'],
    queryFn: () => api.get<Bloqueo[]>(`/aulas/${aulaId}/bloqueos`),
    enabled: !!aulaId,
  });

/**
 * Planificador por centro/fecha
 * - Siempre refetch al montarse
 * - Se invalida/refetch cuando llega el evento global "reserva:updated"
 */
export const usePlanificadorCentro = (centroId: number, fecha: string) => {
  const qc = useQueryClient();

  // invalidar-refetch al producirse cambios en alguna reserva (evento enviado desde ReservationForm)
  useEffect(() => {
    if (!centroId || !fecha) return;
    const onUpdated = () => {
      qc.invalidateQueries({ queryKey: ['planificador', centroId, fecha] });
    };
    window.addEventListener('reserva:updated', onUpdated as EventListener);
    return () => window.removeEventListener('reserva:updated', onUpdated as EventListener);
  }, [qc, centroId, fecha]);

  return useQuery<PlanificadorData>({
    queryKey: ['planificador', centroId, fecha],
    queryFn: () =>
      api.get<PlanificadorData>(
        `/aulas/planificador/centro/${centroId}?fecha=${encodeURIComponent(fecha)}`,
      ),
    enabled: !!centroId && !!fecha,
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
};

/** ---------- Mutations: Aulas ---------- */
export const useCreateAula = (centroId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AulaInput) => api.post<Aula>('/aulas', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['centros', centroId, 'aulas'] });
    },
  });
};

export const useUpdateAula = (centroId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number } & Partial<AulaInput>) =>
      api.put<Aula>(`/aulas/${input.id}`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['aulas', vars.id] });
      qc.invalidateQueries({ queryKey: ['centros', centroId, 'aulas'] });
    },
  });
};

export const useToggleAula = (centroId: number) => {
  const qc = useQueryClient();

  const isTrue = (v: unknown) => v === true || v === 1 || v === '1';

  return useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      api.patch(`/aulas/${id}/${activo ? 'desactivar' : 'activar'}`),

    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: ['centros', centroId, 'aulas'] });
      await qc.cancelQueries({ queryKey: ['aulas', id] });

      const prevLista = qc.getQueryData<any[]>(['centros', centroId, 'aulas']);
      const prevAula = qc.getQueryData<any>(['aulas', id]);

      if (prevLista) {
        const nextLista = prevLista.map((a) =>
          a.id === id ? { ...a, activo: isTrue(a.activo) ? 0 : 1 } : a,
        );
        qc.setQueryData(['centros', centroId, 'aulas'], nextLista);
      }
      if (prevAula) {
        qc.setQueryData(['aulas', id], {
          ...prevAula,
          activo: isTrue(prevAula.activo) ? 0 : 1,
        });
      }

      return { prevLista, prevAula };
    },

    onError: (_err, { id }, ctx) => {
      if (ctx?.prevLista) qc.setQueryData(['centros', centroId, 'aulas'], ctx.prevLista);
      if (ctx?.prevAula) qc.setQueryData(['aulas', id], ctx.prevAula);
    },

    onSuccess: (data, { id }) => {
      if (data) {
        qc.setQueryData(['aulas', id], data);
        const list = qc.getQueryData<any[]>(['centros', centroId, 'aulas']);
        if (list) {
          const next = list.map((a) => (a.id === id ? { ...a, ...data } : a));
          qc.setQueryData(['centros', centroId, 'aulas'], next);
        }
      }
    },

    onSettled: (_d, _e, { id }) => {
      qc.invalidateQueries({ queryKey: ['aulas', id] });
      qc.invalidateQueries({ queryKey: ['centros', centroId, 'aulas'] });
      qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'planificador',
      });
    },
  });
};

export const useDeleteAula = (centroId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/aulas/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['centros', centroId, 'aulas'] });
      qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'planificador',
      });
    },
  });
};

/** ---------- Mutations: Bloqueos ---------- */
const invalidatePlanner = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({
    predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'planificador',
  });

export const useCreateBloqueo = (aulaId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<Bloqueo, 'id' | 'id_aula'> & { dow: number }) =>
      api.post<Bloqueo>(`/aulas/${aulaId}/bloqueos`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aulas', aulaId, 'bloqueos'] });
      invalidatePlanner(qc);
    },
  });
};

export const useUpdateBloqueo = (aulaId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number } & Partial<Bloqueo>) =>
      api.put<Bloqueo>(`/aulas/bloqueos/${input.id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aulas', aulaId, 'bloqueos'] });
      invalidatePlanner(qc);
    },
  });
};

export const useDeleteBloqueo = (aulaId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bloqueoId: number) => api.del(`/aulas/bloqueos/${bloqueoId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aulas', aulaId, 'bloqueos'] });
      invalidatePlanner(qc);
    },
  });
};

/** ---------- Mutations: Asignación por reserva ---------- */
export const useAsignacionAuto = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reservaId: number) => api.post(`/reservas/${reservaId}/aula/auto`),
    onSuccess: (_data, reservaId) => {
      qc.invalidateQueries({ queryKey: ['reservas', reservaId] });
      qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'planificador',
      });
    },
  });
};

export const useAsignacionManual = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reservaId, aulaId }: { reservaId: number; aulaId: number }) =>
      api.post(`/reservas/${reservaId}/aula/manual/${aulaId}`),
    onSuccess: (_data, { reservaId }) => {
      qc.invalidateQueries({ queryKey: ['reservas', reservaId] });
      qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'planificador',
      });
    },
  });
};

export const useAsignacionManualProfesor = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reservaId, aulaId }: { reservaId: number; aulaId: number }) =>
      api.post(`/reservas/${reservaId}/aula/manual/prof/${aulaId}`),
    onSuccess: (_data, { reservaId }) => {
      qc.invalidateQueries({ queryKey: ['reservas', reservaId] });
      qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'planificador',
      });
    },
  });
};

export const useAsignacionManualPropagar = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      reservaId: number;
      aulaId: number;
      body?: { scope?: 'RECURRENCIA' | 'RANGO'; desde?: string; hasta?: string };
    }) =>
      api.post(
        `/reservas/${input.reservaId}/aula/manual/${input.aulaId}/propagar`,
        input.body ?? {},
      ),
    onSuccess: (_data, { reservaId }) => {
      qc.invalidateQueries({ queryKey: ['reservas', reservaId] });
      qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'planificador',
      });
    },
  });
};

// ─────────────────────────────────────────────────────────────
// BULK AUTO ASSIGN (legacy endpoint opcional)
// ─────────────────────────────────────────────────────────────
export type AutoAssignBulkInput = {
  desde: string;
  hasta?: string;
  byDay?: boolean;
  centroId?: number;
};

export const useAutoAssignBulk = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AutoAssignBulkInput) => api.post(`/jobs/aulas/auto-assign`, input),
    onSuccess: () => {
      qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'planificador',
      });
      qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && String(q.queryKey[0]).startsWith('reservas'),
      });
    },
  });
};

// ─────────────────────────────────────────────────────────────
// BULK AUTO ASSIGN – NUEVO job en background (back actual)
// ─────────────────────────────────────────────────────────────
export type BulkJobStartBody = {
  desde: string;
  hasta?: string;
  centroId?: number;
  concurrency?: number;
  pageSize?: number;
};

export type BulkJobStatus = {
  jobId: string;
  status: 'PENDING' | 'RUNNING' | 'DONE' | 'ERROR' | 'CANCELLED';
  params: {
    desde: string;
    hasta?: string;
    centroId?: number;
    concurrency: number;
    pageSize: number;
  };
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  total: number;
  processed: number;
  ok: number;
  errors: number;
  lastError?: string;
  lastReservaId?: number;
  errorDetails?: Array<{ reservaId: number; message: string }>;
};

export const useStartAutoAssignJob = () =>
  useMutation({
    mutationFn: (body: BulkJobStartBody) =>
      api.post<{ jobId: string }>(`/aulas/assign/auto/bulk`, body),
  });

export const useAutoAssignJobStatus = (jobId: string | null) =>
  useQuery<BulkJobStatus>({
    queryKey: ['bulk-assign', jobId ?? ''],
    queryFn: () => api.get<BulkJobStatus>(`/aulas/assign/auto/bulk/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data as BulkJobStatus | undefined;
      return data && (data.status === 'RUNNING' || data.status === 'PENDING') ? 1500 : false;
    },
  });

export const useCancelAutoAssignJob = () =>
  useMutation({
    mutationFn: (jobId: string) => api.post(`/aulas/assign/auto/bulk/${jobId}/cancel`),
  });
